import { currency } from "./helpers";
import i18n from "./i18n";
import colors from "@/stylesheets/colors.module.scss";
import { STATUS_OPTIONS } from "./maintenance_plan_action";
import { Chart } from "react-chartjs-2";

const tooltipColorSettings = {
  displayColors: false,
  backgroundColor: "rgba(235,235,235,0.9)",
  titleFontColor: "#000",
  bodyFontColor: "#000",
  footerFontColor: "#000",
};

const barLegendSettings = {
  position: "bottom",
  labels: {
    boxWidth: 15,
    fontSize: 13,
    generateLabels: function (chart) {
      const labels = Chart.defaults.global.legend.labels.generateLabels(chart);

      return labels.map((label) => {
        const dataset = chart.data.datasets[label.datasetIndex];

        if (dataset.type === "line" || dataset.isOverlay) {
          label.lineWidth = 2;
          label.strokeStyle = dataset.borderColor;
          label.fillStyle = "transparent";
          label.lineDash = dataset.borderDash || [4, 4];
        }

        return label;
      });
    },
  },
  onHover: (event) => {
    event.target.style.cursor = "pointer";
  },
};

const legendSettings = {
  position: "bottom",
  labels: { boxWidth: 15, fontSize: 13 },
  onHover: (event) => {
    event.target.style.cursor = "pointer";
  },
};

const eventSettings = {
  events: ["mousemove", "click"],
  onHover: (event) => {
    event.target.style.cursor = "default";
  },
};

const CHART_COLORS = [
  "rgba(255, 185, 42, 1)",
  "rgba(51, 51, 51, 1)",
  "rgba(243, 104, 14, 1)",
  "rgba(10, 142, 142, 1)",
  "rgba(6, 192, 0, 1)",
  "rgba(38, 238, 178, 1)",
  "rgba(197, 2, 177, 1)",
  "rgba(175, 175, 255, 1)",
  "rgba(251, 98, 135, 1)",
  "rgba(38, 178, 255, 1)",
  "rgba(37, 0, 255, 1)",
  "rgba(204, 204, 204, 1)",
  "rgba(205, 249, 94, 1)",
  "rgba(169, 113, 0, 1)",
  "rgba(216, 192, 106, 1)",
  "rgba(255, 176, 242, 1)",
  "rgba(255, 230, 192, 1)",
];

const getChartColorsFromStatuses = (statuses) => {
  const colorMap = {
    prioritized: colors.red,
    deferred: colors.yellow,
    decided: colors.blue,
    in_progress: colors.light_blue,
    completed: colors.green,
    inactive: colors.soft_gray,
    planned: colors.light_gray,
  };

  return statuses.map((status) => {
    const item = STATUS_OPTIONS.find((option) => option.label == status);
    return colorMap[item.value];
  });
};

const getColor = (index, label = null, preferredColors = {}) => {
  if (preferredColors.hasOwnProperty(label)) {
    return preferredColors[label];
  }
  return CHART_COLORS[
    (index + Object.keys(preferredColors).length) % CHART_COLORS.length
  ];
};

const preferredEnergySavingsColors = () => {
  return {
    [i18n.t("js.lib.chart_helpers.energy_savings")]: colors.energy_savings,
  };
};

const preferredActionTypeColors = () => {
  return {
    [i18n.t("js.lib.chart_helpers.maintenance")]: getColor(0),
    [i18n.t("js.lib.chart_helpers.investment")]: getColor(1),
  };
};

const resolveCssColor = (variableName, fallback) =>
  getComputedStyle(document.documentElement)
    .getPropertyValue(variableName)
    .trim() || fallback;

const preferredBudgetStatusColors = (series) => ({
  [series[0]?.label]: resolveCssColor(
    "--puts--color--icon--blue--base",
    "#23435c"
  ),
  [series[1]?.label]: resolveCssColor("--puts--color--blue-500", "#5990c0"),
  [series[2]?.label]: resolveCssColor(
    "--puts--color--bg--neutral--base",
    "#dedede"
  ),
});

const getLabel = (label) => {
  switch (label) {
    case "maintenance":
      return i18n.t("js.lib.chart_helpers.maintenance");
    case "investment":
      return i18n.t("js.lib.chart_helpers.investment");
    case null:
      return i18n.t("js.lib.chart_helpers.misc");
    default:
      return label;
  }
};

const seriesToChartData = (data) =>
  Object.keys(data).map((year) => ({
    x: new Date(year),
    y: data[year],
  }));

const sumBarLabel = (index, data, formatter = currency) => {
  const sum = data.datasets.reduce(
    (sum, dataset) => sum + dataset.data[index].y,
    0
  );
  return `${i18n.t("js.lib.chart_helpers.total")}: ${formatter(sum, true)}`;
};

const barLabel = (tooltipItem, data, formatter = currency) => {
  const dataset = data.datasets[tooltipItem.datasetIndex];
  const cost = formatter(dataset.data[tooltipItem.index].y);
  const label = dataset.tooltipLabel || dataset.label;

  return label ? `${getLabel(String(label))}: ${cost}` : cost;
};

export {
  getColor,
  preferredEnergySavingsColors,
  preferredActionTypeColors,
  getLabel,
  CHART_COLORS,
  getChartColorsFromStatuses,
  sumBarLabel,
  tooltipColorSettings,
  legendSettings,
  barLegendSettings,
  eventSettings,
  seriesToChartData,
  barLabel,
  preferredBudgetStatusColors,
};
